import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FileText, Calendar, Shield, TrendingUp, Users, Award, Info, DollarSign, Home, Building, Briefcase, CheckCircle } from "lucide-react";
import { differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds, addDays, startOfDay } from "date-fns";
import PitchDeckModal from "@/components/PitchDeckModal";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
const Invest = () => {
  const [selectedRound, setSelectedRound] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Get campaign start time (when investor clicked "Invest" on /intro)
  // For demo purposes, using a fixed start time - in production this would be stored when they click "Invest"
  const campaignStartTime = useState(() => {
    // Check if there's a stored campaign start time
    const stored = localStorage.getItem('iSPAI_campaign_start');
    if (stored) {
      return new Date(stored);
    }
    // If no stored time, start campaign now (first visit to /invest page)
    const startTime = new Date();
    localStorage.setItem('iSPAI_campaign_start', startTime.toISOString());
    return startTime;
  })[0];

  // Calculate campaign end time (5 days from start, at midnight)
  const campaignEndTime = addDays(startOfDay(campaignStartTime), 5);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculate which day of campaign we're in and current discount
  const getDynamicDiscount = () => {
    const daysPassed = differenceInDays(currentTime, campaignStartTime);
    if (daysPassed >= 5) return {
      discount: 0,
      expired: true,
      day: 5
    };
    const discounts = [20, 15, 10, 5, 0];
    return {
      discount: discounts[Math.min(daysPassed, 4)],
      expired: false,
      day: daysPassed + 1
    };
  };

  // Calculate time remaining until next midnight (when discount changes)
  const getTimeUntilMidnight = () => {
    const now = new Date();
    const nextMidnight = new Date(now);
    nextMidnight.setHours(24, 0, 0, 0); // Set to next midnight

    const totalSeconds = differenceInSeconds(nextMidnight, now);
    if (totalSeconds <= 0) return {
      hours: 0,
      minutes: 0,
      seconds: 0
    };
    const hours = Math.floor(totalSeconds / (60 * 60));
    const minutes = Math.floor(totalSeconds % (60 * 60) / 60);
    const seconds = totalSeconds % 60;
    return {
      hours,
      minutes,
      seconds
    };
  };

  // Get next discount amount
  const getNextDiscount = (currentDiscount: number) => {
    if (currentDiscount <= 0) return "Expires";
    return `${Math.max(0, currentDiscount - 5)}%`;
  };
  const {
    discount: currentDiscount,
    expired: discountExpired,
    day: currentDay
  } = getDynamicDiscount();
  const timeUntilMidnight = getTimeUntilMidnight();
  const nextDiscount = getNextDiscount(currentDiscount);
  const [showPitchDeckModal, setShowPitchDeckModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    amount: ""
  });
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - window.innerHeight / 2 + elementRect.height / 2;
      window.scrollTo({
        top: middle,
        behavior: 'smooth'
      });
    }
  };
  const investmentRounds = [{
    name: "Pre-Seed",
    amount: "$2M",
    description: "Early stage validation and team building",
    minimum: "$100K"
  }, {
    name: "Seed",
    amount: "$5M",
    description: "Product-market fit and initial scale",
    minimum: "$250K"
  }, {
    name: "Series A",
    amount: "$15M",
    description: "Scale operations and market expansion",
    minimum: "$500K"
  }];
  const documents = [{
    title: "Executive Summary",
    icon: "📄",
    description: "High-level overview of iSPAI's vision and market opportunity"
  }, {
    title: "Pitch Deck",
    icon: "📊",
    description: "Comprehensive presentation of our business model and growth strategy"
  }, {
    title: "Technical Vision",
    icon: "🔧",
    description: "Deep dive into our AI-enabled knowledge network platform"
  }];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Investment inquiry submitted:", {
      ...formData,
      selectedRound
    });
    // TODO: Implement Supabase integration for form submission
  };
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  return <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 to-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }}>
            <div className="flex justify-center mb-8">
              <img src="/lovable-uploads/a1b437ed-b1ee-4970-8136-8ffa5ecea125.png" alt="iSPAI Logo" className="h-32 w-auto" />
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Integrated Special Purpose AI
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">Join us in building the world's first AI-enabled knowledge network and marketplace for the wisdom of the workforce. Together we will enable better enterprise-grade AI while solving workforce fear of my obsolescence (FOMO 2.0).</p>
            
        <div className="flex justify-center mt-12 mb-64">
          <Button onClick={() => scrollToSection("why-ispai")} size="lg" className="bg-black text-white hover:bg-gray-800 text-lg">
            Why iSPAI?
          </Button>
        </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">


        {/* Value Proposition */}
        <motion.section id="why-ispai" className="mb-48" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }}>
          <h2 className="font-bold text-gray-900 mb-8 text-center text-4xl">Why iSPAI?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <ImageHoverPopup imageSrc="/lovable-uploads/a476b636-970d-4d96-af86-f39598f52739.png" imageAlt="Market Analysis - TAM, SAM, SOM breakdown for AI, IP, Web3, and Knowledge Management markets">
              <Card className="border-2 border-gray-200 hover:border-gray-300 transition-all h-full">
                <CardHeader>
                  <Users className="w-12 h-12 text-blue-600 mb-4" />
                  <CardTitle>Massive Market Opportunity</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">$2T+ TAM with urgent need for knowledge preservation as workforce retires and automation accelerates.</p>
                </CardContent>
              </Card>
            </ImageHoverPopup>
            <ImageHoverPopup imageSrc="/lovable-uploads/6d92ef7b-f6c6-447d-8a0d-c2a3b4c8fc7f.png" imageAlt="Quote from Louie Ehrlich, Global CIO (ret.) and Angel Investor for iSPAI about AI and blockchain technology">
              <Card className="border-2 border-gray-200 hover:border-gray-300 transition-all h-full">
                <CardHeader>
                  <Shield className="w-12 h-12 text-green-600 mb-4" />
                  <CardTitle>Proprietary Technology</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Operating at the intersection of AI, Web3, and the Workforce, each iSPAI component has been proven outside the enterprise.</p>
                </CardContent>
              </Card>
            </ImageHoverPopup>
            <ImageHoverPopup imageSrc="/lovable-uploads/aca12ada-ceaa-4f9f-a75e-e6f8a973d088.png" imageAlt="CURT - Client companies and partnership network" position="bottom">
              <Card className="border-2 border-gray-200 hover:border-gray-300 transition-all h-full">
                <CardHeader>
                  <TrendingUp className="w-12 h-12 text-purple-600 mb-4" />
                  <CardTitle>Traction-Building Momentum</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Strong pipeline of enterprises and agencies with a well-defined path forward to iterate PMF with users.</p>
                </CardContent>
              </Card>
            </ImageHoverPopup>
          </div>
          
          <div className="flex justify-center mt-8 mb-64">
            <Button onClick={() => scrollToSection("why-now")} size="lg" className="bg-black text-white hover:bg-gray-800 text-lg">
              Why Now?
            </Button>
          </div>
        </motion.section>

        {/* Why Now Section */}
        <motion.section id="why-now" className="mb-48" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }}>
          <h2 className="font-bold text-gray-900 mb-8 text-center text-4xl">Why Now?</h2>
          <div className="flex justify-center">
            <img src="/lovable-uploads/64817a8f-4953-4072-89eb-02ab4cee3fa9.png" alt="The Pain, and the Possibility - AI quotes from Jim Farley and Sam Altman" className="max-w-full h-auto rounded-lg shadow-lg" />
          </div>
          
          <div className="flex justify-center mt-8 mb-0">
            <Button onClick={() => scrollToSection("company-details")} size="lg" className="bg-black text-white hover:bg-gray-800 text-lg">
              About iSPAI
            </Button>
          </div>
        </motion.section>



        {/* Documents Section */}
        <motion.section id="company-details" className="mb-16" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.4
      }}>
          <h2 className="font-bold text-center mb-12 text-black text-4xl">About iSPAI</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {documents.map((doc, index) => {
              const content = (
                <motion.div key={index} className="group cursor-pointer" onClick={() => {
                  if (doc.title === "Pitch Deck") {
                    setShowPitchDeckModal(true);
                  }
                }} whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }}>
                  <div className="bg-white border border-gray-200 rounded-xl p-6 h-full shadow-sm hover:shadow-lg transition-all duration-300 group-hover:border-blue-300">
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="text-4xl">{doc.icon}</div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2 text-black group-hover:text-blue-600 transition-colors">
                          {doc.title}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {doc.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );

              if (doc.title === "Executive Summary") {
                return (
                  <ImageHoverPopup 
                    key={index}
                    imageSrc="/lovable-uploads/9917dbf9-dcca-46cb-a6a3-520c6b3e4ef3.png" 
                    imageAlt="Executive Summary - Company Overview, Product Overview, and Revenue Model"
                    size="large"
                  >
                    {content}
                  </ImageHoverPopup>
                );
              }

              return content;
            })}
          </div>
        </motion.section>

        <div className="flex justify-center mt-8 mb-64">
          <Button onClick={() => scrollToSection("current-terms")} size="lg" className="bg-black text-white hover:bg-gray-800 text-lg">
            Terms Offered
          </Button>
        </div>
        {/* SAFE Terms - Prominent Display */}
        <motion.section id="current-terms" className="mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.0
      }}>
          <div className="bg-white border-2 border-black rounded-xl p-8">
            <h2 className="font-bold text-center mb-6 text-black text-4xl">Current Investment Terms Offered</h2>
            <div className="text-center mb-6">
              <div className="text-sm text-black">
                Discount offered will reduce by 5% at midnight. All discounts expire after five days.
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-black mb-2">$10M</div>
                <div className="text-sm text-black">Pre-Money Valuation</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-black mb-2">{currentDiscount}%</div>
                <div className="text-sm text-black">Current Discount Rate</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-black mb-2">$100K</div>
                <div className="text-sm text-black">Minimum Investment</div>
              </div>
            </div>
            <div className="text-center mt-6">
              <Button size="lg" className="bg-black hover:bg-gray-800 text-white px-8 py-3 text-lg font-semibold" onClick={() => scrollToSection("request-terms")}>
                Request Term Sheet
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-6 max-w-lg mx-auto mt-6 pt-6 border-t border-gray-200">
              <div className="text-center">
                <div className="text-3xl font-bold text-black mb-2">{timeUntilMidnight.hours}h {timeUntilMidnight.minutes}m {timeUntilMidnight.seconds}s</div>
                <div className="text-sm text-black">Countdown To Midnight</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-black mb-2">{nextDiscount}</div>
                <div className="text-sm text-black">Next Discount</div>
              </div>
            </div>
          </div>
        </motion.section>

        


        {/* Space between sections */}
        <div className="mb-32"></div>


        {/* Investment Action Section */}
        <motion.section id="request-terms" className="mb-32" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.8
      }}>
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 border border-gray-200">
            <h2 className="font-bold text-center mb-8 text-4xl text-gray-900">Secure Your Investment</h2>
            
            {/* Accredited Investor Verification */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="text-yellow-600 text-xl">⚠️</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-yellow-800">Accredited Investor Requirement</h4>
                    <Dialog>
                      <DialogTrigger asChild>
                        <button className="text-blue-600 hover:text-blue-800 transition-colors">
                          <Info className="w-4 h-4" />
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white">
                        <DialogHeader>
                          <DialogTitle className="text-xl font-bold text-center mb-4">
                            Who Qualifies as an Accredited Investor under SEC Rule 501?
                          </DialogTitle>
                        </DialogHeader>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Individual Investor Criteria */}
                          <div className="bg-gray-50 rounded-lg p-6">
                            <div className="flex items-center gap-2 mb-4">
                              <Users className="w-6 h-6 text-blue-600" />
                              <h3 className="text-lg font-semibold">Individual Investor Criteria</h3>
                            </div>
                            
                            <div className="space-y-4">
                              <div className="flex items-start gap-3">
                                <DollarSign className="w-5 h-5 text-green-600 mt-1" />
                                <div>
                                  <h4 className="font-medium">Income Test</h4>
                                  <p className="text-sm text-gray-600">Earned $200,000/year ($300,000 with spouse) for the last 2 years</p>
                                </div>
                              </div>
                              
                              <div className="flex items-start gap-3">
                                <Home className="w-5 h-5 text-green-600 mt-1" />
                                <div>
                                  <h4 className="font-medium">Net Worth Test</h4>
                                  <p className="text-sm text-gray-600">Net worth over $1 million, excluding primary residence</p>
                                </div>
                              </div>
                              
                              <div className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                                <div>
                                  <h4 className="font-medium">Professional Credentials</h4>
                                  <p className="text-sm text-gray-600">Hold Series 7, 65, or 82 licenses</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Entity Investor Criteria */}
                          <div className="bg-gray-50 rounded-lg p-6">
                            <div className="flex items-center gap-2 mb-4">
                              <Building className="w-6 h-6 text-blue-600" />
                              <h3 className="text-lg font-semibold">Entity Investor Criteria</h3>
                            </div>
                            
                            <div className="space-y-4">
                              <div className="flex items-start gap-3">
                                <Briefcase className="w-5 h-5 text-green-600 mt-1" />
                                <div>
                                  <h4 className="font-medium">Financial Institutions / Funds</h4>
                                  <p className="text-sm text-gray-600">Banks, broker-dealers, insurance companies, registered investment funds</p>
                                </div>
                              </div>
                              
                              <div className="flex items-start gap-3">
                                <DollarSign className="w-5 h-5 text-green-600 mt-1" />
                                <div>
                                  <h4 className="font-medium">Private Entities</h4>
                                  <p className="text-sm text-gray-600">Assets over $5 million, not formed for the specific purpose of this investment</p>
                                </div>
                              </div>
                              
                              <div className="flex items-start gap-3">
                                <Users className="w-5 h-5 text-green-600 mt-1" />
                                <div>
                                  <h4 className="font-medium">Knowledgeable Employees</h4>
                                  <p className="text-sm text-gray-600">Certain employees of private funds qualify individually</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Compliance Reminder */}
                        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Shield className="w-5 h-5 text-blue-600" />
                            <h3 className="font-medium text-blue-800">Compliance Reminder</h3>
                          </div>
                          <p className="text-blue-700 text-sm">
                            Accredited Investor status must be verified for participation in 506(c) offerings.
                          </p>
                        </div>
                        
                        {/* Disclaimer */}
                        <div className="mt-4 text-xs text-gray-500 text-center border-t pt-4">
                          This is a simplified summary. Refer to SEC Rule 501 of Regulation D for the complete definition.
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <p className="text-yellow-700 text-sm">This offering is limited to accredited investors as defined by SEC regulations. Please confirm your status below.</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-6 text-black">Investor Information</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <Input type="text" required value={formData.name} onChange={e => handleInputChange('name', e.target.value)} className="w-full" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <Input type="email" required value={formData.email} onChange={e => handleInputChange('email', e.target.value)} className="w-full" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Investment Amount *</label>
                    <Input type="text" placeholder="$100,000 minimum" required value={formData.amount} onChange={e => handleInputChange('amount', e.target.value)} className="w-full" />
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" id="accredited" required className="rounded" />
                    <label htmlFor="accredited" className="text-sm text-gray-700">
                      I confirm that I am an accredited investor as defined by SEC Rule 501
                    </label>
                  </div>
                  <Button type="submit" size="lg" className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3">
                    Request Carta Term Sheet
                  </Button>
                </form>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-6 text-black">Next Steps</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                    <div>
                      <h4 className="font-medium text-gray-900">Term Sheet Review</h4>
                      <p className="text-gray-600 text-sm">Receive detailed SAFE agreement via Carta within 24 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                    <div>
                      <h4 className="font-medium text-gray-900">Due Diligence</h4>
                      <p className="text-gray-600 text-sm">Schedule investor call and access data room</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                    <div>
                      <h4 className="font-medium text-gray-900">Investment Execution</h4>
                      <p className="text-gray-600 text-sm">Sign documents and transfer funds through Carta</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">⚡ Limited Time Bonus</h4>
                  <p className="text-gray-700 text-sm mb-2">Early investors receive:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Discount on an iSPAI priced round</li>
                    <li>• Priority access to iSPAI's Seed round</li>
                    <li>• Exclusive access to the iSPAI data room in Carta</li>
                    <li>• Beta platform access</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.section>
      </div>


      {/* Pitch Deck Modal */}
      <PitchDeckModal open={showPitchDeckModal} onOpenChange={setShowPitchDeckModal} />
    </div>;
};
export default Invest;